import { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearFormDraft,
  formatDraftTimestamp,
  loadFormDraft,
  saveFormDraft,
} from '../utils/formDraftStorage';

/**
 * Local draft persistence for create forms (localStorage, per user).
 * File uploads are not stored — only serializable field values.
 */
export default function useFormDraft({
  formKey,
  userId,
  isEditMode = false,
  allowEditDraft = false,
  enabled = true,
  getSnapshot,
  applySnapshot,
  resetForm = null,
}) {
  const [restoredAt, setRestoredAt] = useState(null);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [saveFlash, setSaveFlash] = useState('');
  const loadedRef = useRef(false);
  const getSnapshotRef = useRef(getSnapshot);
  const applySnapshotRef = useRef(applySnapshot);
  getSnapshotRef.current = getSnapshot;
  applySnapshotRef.current = applySnapshot;

  const canSaveDraft = Boolean(
    enabled && userId != null && getSnapshot && applySnapshot && (!isEditMode || allowEditDraft),
  );

  useEffect(() => {
    if (loadedRef.current || (isEditMode && !allowEditDraft) || !enabled || userId == null) return;
    if (!getSnapshotRef.current || !applySnapshotRef.current) return;
    loadedRef.current = true;
    const draft = loadFormDraft(formKey, userId);
    if (draft?.data) {
      applySnapshotRef.current(draft.data);
      setRestoredAt(draft.savedAt);
      setLastSavedAt(draft.savedAt);
    }
  }, [formKey, userId, isEditMode, allowEditDraft, enabled]);

  const saveDraft = useCallback(() => {
    if (!canSaveDraft) return false;
    const data = getSnapshot();
    const savedAt = saveFormDraft(formKey, userId, data);
    if (!savedAt) return false;
    setLastSavedAt(savedAt);
    setSaveFlash(`Draft saved at ${formatDraftTimestamp(savedAt)}`);
    return true;
  }, [canSaveDraft, formKey, userId, getSnapshot]);

  const clearDraft = useCallback(() => {
    if (userId == null) return;
    clearFormDraft(formKey, userId);
    setRestoredAt(null);
    setLastSavedAt(null);
    setNoticeDismissed(true);
    setSaveFlash('');
  }, [formKey, userId]);

  const discardDraft = useCallback(() => {
    clearDraft();
    resetForm?.();
  }, [clearDraft, resetForm]);

  const dismissNotice = useCallback(() => {
    setNoticeDismissed(true);
  }, []);

  const showNotice = Boolean(canSaveDraft && restoredAt && !noticeDismissed);

  return {
    canSaveDraft,
    saveDraft,
    clearDraft,
    discardDraft,
    dismissNotice,
    showNotice,
    restoredAt,
    lastSavedAt,
    saveFlash,
    formatDraftTimestamp,
  };
}
